import { lazy, Suspense, useEffect, useMemo, useRef, useState, type FormEvent, type MouseEvent as ReactMouseEvent } from 'react';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { AnimatePresence, motion } from 'motion/react';
import {
  Activity,
  ArrowDown,
  ArrowRight,
  Check,
  ChevronDown,
  CirclePause,
  Command,
  Contrast,
  Gauge,
  Menu,
  MousePointer2,
  Pause,
  Play,
  Radar,
  Send,
  Sparkles,
  VolumeX,
  X,
  Zap,
} from 'lucide-react';
import {
  budgetOptions,
  businessOptions,
  capabilities,
  cases,
  faqs,
  featureOptions,
  impactOptions,
  objectiveOptions,
  packages,
  testimonials,
  timelineOptions,
} from './data';
import { buildRecommendation, initialConfiguratorState, recommendationWhatsApp } from './configurator';
import { useExperienceStore } from './store';
import {
  CinematicScroll,
  DigitalArtifacts,
  ExperienceMap,
  LivingInterfaces,
  MotionLaboratory,
} from './ShowcaseSections';
import type { ConfiguratorState, Recommendation } from './types';

gsap.registerPlugin(ScrollTrigger);

const HeroScene = lazy(() => import('./HeroScene'));
const WHATSAPP = 'https://wa.me/5491155550190?text=Hola%20NODO%20CERO%2C%20quiero%20conversar%20sobre%20una%20experiencia%20digital.';

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function useMagnetic() {
  const onMove = (event: ReactMouseEvent<HTMLElement>) => {
    if (window.matchMedia('(pointer: coarse)').matches) return;
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    const x = event.clientX - rect.left - rect.width / 2;
    const y = event.clientY - rect.top - rect.height / 2;
    target.style.transform = `translate(${x * 0.13}px, ${y * 0.13}px)`;
  };
  const onLeave = (event: ReactMouseEvent<HTMLElement>) => {
    event.currentTarget.style.transform = '';
  };
  return { onMouseMove: onMove, onMouseLeave: onLeave };
}

function Loader() {
  const [visible, setVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const reducedMotion = useExperienceStore((state) => state.reducedMotion);

  useEffect(() => {
    const duration = reducedMotion ? 250 : 1350;
    const started = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const value = Math.min(100, Math.round(((now - started) / duration) * 100));
      setProgress(value);
      if (value < 100) frame = requestAnimationFrame(tick);
      else setTimeout(() => setVisible(false), reducedMotion ? 40 : 260);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [reducedMotion]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="loader"
          initial={{ opacity: 1 }}
          exit={{ y: '-100%', transition: { duration: reducedMotion ? 0.01 : 0.75, ease: [0.76, 0, 0.24, 1] } }}
          aria-label="Cargando experiencia"
        >
          <button className="loader-skip" onClick={() => setVisible(false)}>Omitir</button>
          <div className="loader-mark">
            <div className="loader-orbit" />
            <span>0</span>
          </div>
          <div className="loader-word">
            <strong>NODO CERO</strong>
            <span>Inicializando señal / {String(progress).padStart(3, '0')}</span>
          </div>
          <div className="loader-track"><i style={{ width: `${progress}%` }} /></div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { impactMode, setImpactMode, setCommandOpen } = useExperienceStore();
  const magnetic = useMagnetic();
  const links = [
    ['Enfoque', 'manifiesto'],
    ['Exhibición', 'artefactos'],
    ['Laboratorio', 'movimiento'],
    ['Configurador', 'configurador'],
    ['Contacto', 'contacto'],
  ];

  return (
    <header className="site-header">
      <a href="#inicio" className="brand" aria-label="NODO CERO, ir al inicio">
        <i />
        <span>NODO CERO</span>
        <small>AR / 34.6°S</small>
      </a>
      <nav className={mobileOpen ? 'nav open' : 'nav'} aria-label="Navegación principal">
        {links.map(([label, id]) => (
          <button key={id} onClick={() => { scrollToSection(id); setMobileOpen(false); }}>{label}</button>
        ))}
      </nav>
      <div className="header-actions">
        <div className="mode-switch" aria-label="Intensidad visual">
          <button className={impactMode === 'elegante' ? 'active' : ''} onClick={() => setImpactMode('elegante')}>Elegante</button>
          <button className={impactMode === 'impacto' ? 'active' : ''} onClick={() => setImpactMode('impacto')}>Impacto</button>
        </div>
        <button className="command-button" onClick={() => setCommandOpen(true)} aria-label="Abrir comandos" {...magnetic}>
          <Command size={15} /> <span>⌘ K</span>
        </button>
        <button className="menu-button" onClick={() => setMobileOpen(!mobileOpen)} aria-expanded={mobileOpen} aria-label="Abrir menú">
          {mobileOpen ? <X /> : <Menu />}
        </button>
      </div>
    </header>
  );
}

function CommandPalette() {
  const {
    commandOpen,
    setCommandOpen,
    impactMode,
    setImpactMode,
    reducedMotion,
    toggleMotion,
    paused,
    togglePaused,
  } = useExperienceStore();
  const [query, setQuery] = useState('');
  const input = useRef<HTMLInputElement>(null);

  const commands = useMemo(() => [
    { label: 'Ir al inicio', hint: 'Sección', action: () => scrollToSection('inicio') },
    { label: 'Ver capacidades', hint: 'Sección', action: () => scrollToSection('capacidades') },
    { label: 'Explorar objetos digitales', hint: 'Exhibición', action: () => scrollToSection('artefactos') },
    { label: 'Entrar al laboratorio de movimiento', hint: 'Experimento', action: () => scrollToSection('movimiento') },
    { label: 'Abrir mapa de experiencias', hint: 'Exhibición', action: () => scrollToSection('mapa') },
    { label: 'Abrir configurador', hint: 'Herramienta', action: () => scrollToSection('configurador') },
    { label: 'Ver paquetes', hint: 'Comercial', action: () => scrollToSection('paquetes') },
    { label: 'Contactar por WhatsApp', hint: 'Acción', action: () => window.open(WHATSAPP, '_blank', 'noopener') },
    { label: impactMode === 'impacto' ? 'Activar Modo Elegante' : 'Activar Modo Impacto', hint: 'Experiencia', action: () => setImpactMode(impactMode === 'impacto' ? 'elegante' : 'impacto') },
    { label: reducedMotion ? 'Restaurar movimiento' : 'Reducir movimiento', hint: 'Accesibilidad', action: toggleMotion },
    { label: paused ? 'Reanudar animaciones' : 'Pausar animaciones', hint: 'Control', action: togglePaused },
  ], [impactMode, paused, reducedMotion, setImpactMode, toggleMotion, togglePaused]);
  const filtered = commands.filter((command) => command.label.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen(!commandOpen);
      }
      if (event.key === 'Escape') setCommandOpen(false);
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [commandOpen, setCommandOpen]);

  useEffect(() => {
    if (commandOpen) setTimeout(() => input.current?.focus(), 80);
    else setQuery('');
  }, [commandOpen]);

  return (
    <AnimatePresence>
      {commandOpen && (
        <motion.div className="command-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={() => setCommandOpen(false)}>
          <motion.div className="command-panel" role="dialog" aria-modal="true" aria-label="Acciones rápidas" initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 14, scale: 0.98 }} onMouseDown={(event) => event.stopPropagation()}>
            <div className="command-search">
              <Command size={19} />
              <input ref={input} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar una acción..." aria-label="Buscar acción" />
              <kbd>ESC</kbd>
            </div>
            <div className="command-results">
              {filtered.map((command) => (
                <button key={command.label} onClick={() => { command.action(); setCommandOpen(false); }}>
                  <span>{command.label}</span><small>{command.hint}</small>
                </button>
              ))}
              {!filtered.length && <p>No encontramos esa acción.</p>}
            </div>
            <footer><span>↑↓ explorar</span><span>↵ ejecutar</span><span>NODO OS v1.0</span></footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ExperienceControls() {
  const [open, setOpen] = useState(false);
  const {
    reducedMotion,
    reducedParticles,
    paused,
    highContrast,
    toggleMotion,
    toggleParticles,
    togglePaused,
    toggleContrast,
  } = useExperienceStore();

  return (
    <aside className={open ? 'experience-controls open' : 'experience-controls'} aria-label="Controles de experiencia">
      <button className="controls-trigger" onClick={() => setOpen(!open)} aria-expanded={open}>
        <Gauge size={18} /><span>Experiencia</span>
      </button>
      <div className="controls-panel">
        <div><strong>CONTROL DE SEÑAL</strong><button onClick={() => setOpen(false)} aria-label="Cerrar controles"><X size={16} /></button></div>
        <button onClick={toggleMotion}><VolumeX size={17} /><span>Reducir movimiento</span><i className={reducedMotion ? 'on' : ''} /></button>
        <button onClick={toggleParticles}><Sparkles size={17} /><span>Reducir partículas</span><i className={reducedParticles ? 'on' : ''} /></button>
        <button onClick={togglePaused}>{paused ? <Play size={17} /> : <CirclePause size={17} />}<span>{paused ? 'Reanudar' : 'Pausar'} animaciones</span><i className={paused ? 'on' : ''} /></button>
        <button onClick={toggleContrast}><Contrast size={17} /><span>Contraste alto</span><i className={highContrast ? 'on' : ''} /></button>
      </div>
    </aside>
  );
}

function Hero() {
  const magnetic = useMagnetic();
  return (
    <section id="inicio" className="hero" aria-labelledby="hero-title">
      <div className="hero-grid" />
      <div className="hero-scene" aria-hidden="true">
        <Suspense fallback={<div className="webgl-fallback" />}>
          <HeroScene />
        </Suspense>
      </div>
      <div className="hero-meta reveal">
        <span><i /> Oficina de experiencias digitales</span>
        <span>Buenos Aires / Argentina</span>
      </div>
      <div className="hero-copy">
        <p className="eyebrow reveal">Tecnología creativa / estrategia comercial / 001</p>
        <h1 id="hero-title" className="reveal">
          Tu marca no necesita<br />
          <em>más pantalla.</em><br />
          Necesita <span>señal.</span>
        </h1>
        <p className="hero-sub reveal">Diseñamos sitios, lanzamientos y sistemas interactivos para marcas argentinas que quieren dejar de competir por parecido.</p>
        <div className="hero-ctas">
          <button className="button primary magnetic" onClick={() => scrollToSection('configurador')} {...magnetic}>
            Configurar una experiencia <ArrowRight size={18} />
          </button>
          <button className="button ghost magnetic" onClick={() => scrollToSection('casos')} {...magnetic}>
            Explorar el campo <MousePointer2 size={17} />
          </button>
        </div>
      </div>
      <div className="hero-status">
        <div><Activity size={16} /><span>Sistema online</span><strong>60 FPS</strong></div>
        <div><Radar size={16} /><span>Campo activo</span><strong>BA / AR</strong></div>
      </div>
      <button className="scroll-cue" onClick={() => scrollToSection('manifiesto')} aria-label="Continuar hacia el enfoque">
        <span>Desplazar para entrar</span><ArrowDown size={17} />
      </button>
    </section>
  );
}

function SignalMarquee() {
  return (
    <div className="signal-marquee" aria-hidden="true">
      <div>
        {[0, 1].map((set) => (
          <span key={set}>
            MOVIMIENTO CON INTENCIÓN <i /> SISTEMAS QUE CONVIERTEN <i /> EXPERIENCIAS QUE QUEDAN <i /> ARGENTINA / 2026 <i />
          </span>
        ))}
      </div>
    </div>
  );
}

function Manifesto() {
  return (
    <section id="manifiesto" className="manifesto section">
      <div className="section-index reveal"><span>01</span><p>EL PUNTO DE PARTIDA</p><i /></div>
      <div className="manifesto-layout">
        <div className="manifesto-sticky reveal">
          <p className="eyebrow">Una web puede estar online<br />y seguir siendo invisible.</p>
          <div className="signal-radar" aria-label="Visualización de señal digital">
            <div className="radar-ring r1" /><div className="radar-ring r2" /><div className="radar-ring r3" />
            <div className="radar-sweep" />
            <span className="radar-node n1">ATENCIÓN</span>
            <span className="radar-node n2">DESEO</span>
            <span className="radar-node n3">ACCIÓN</span>
            <b>0</b>
          </div>
        </div>
        <div className="manifesto-copy">
          <h2 className="reveal">La diferencia no está en sumar efectos. Está en diseñar <em>qué tiene que sentir, entender y hacer</em> una persona.</h2>
          <div className="manifesto-points">
            <article className="reveal"><span>01 / Atención</span><h3>Interrumpir el automático.</h3><p>Una dirección visual propia convierte los primeros tres segundos en una declaración de valor.</p></article>
            <article className="reveal"><span>02 / Argumento</span><h3>Hacer visible lo intangible.</h3><p>Movimiento, interacción y estructura explican lo que una lista de servicios nunca podría.</p></article>
            <article className="reveal"><span>03 / Acción</span><h3>Convertir sin romper el clima.</h3><p>Cada interacción conduce hacia una decisión: explorar, comparar, configurar, consultar.</p></article>
          </div>
        </div>
      </div>
    </section>
  );
}

function BeforeAfter() {
  const [position, setPosition] = useState(52);
  return (
    <section className="gap-section section">
      <div className="gap-copy reveal">
        <span className="eyebrow">Brecha de presencia / simulador 01</span>
        <h2>De “correcto” a<br /><em>imposible de confundir.</em></h2>
        <p>Arrastrá para ver qué cambia cuando una marca deja de ocupar una página y empieza a dirigir una experiencia.</p>
      </div>
      <div className="compare reveal" style={{ '--split': `${position}%` } as React.CSSProperties}>
        <div className="compare-base">
          <div className="generic-site">
            <header><b>EMPRESA</b><span>Inicio &nbsp; Servicios &nbsp; Contacto</span></header>
            <main><small>Soluciones para tu empresa</small><h3>Calidad y compromiso</h3><p>Brindamos el mejor servicio para nuestros clientes.</p><button>Conocer más</button></main>
          </div>
        </div>
        <div className="compare-premium">
          <div className="premium-orbit" /><div className="premium-grid" />
          <span>SEÑAL 98.4%</span><h3>HACÉ<br />VISIBLE<br /><em>LA DIFERENCIA.</em></h3><button>ENTRAR AL SISTEMA ↗</button>
        </div>
        <input type="range" min="8" max="92" value={position} onChange={(event) => setPosition(Number(event.target.value))} aria-label="Comparar sitio genérico y experiencia premium" />
        <div className="compare-handle"><span>↔</span></div>
        <div className="compare-labels"><span>WEB GENÉRICA</span><span>EXPERIENCIA PROPIA</span></div>
      </div>
    </section>
  );
}

function Capabilities() {
  const [active, setActive] = useState(capabilities[0].id);
  return (
    <section id="capacidades" className="capabilities section">
      <div className="section-index reveal"><span>02</span><p>CAPACIDADES</p><i /></div>
      <div className="section-heading reveal">
        <div><span className="eyebrow">No vendemos efectos.</span><h2>Construimos<br /><em>ventaja perceptiva.</em></h2></div>
        <p>Una combinación deliberada de estrategia, diseño, ingeniería y movimiento para cambiar cómo se entiende una marca.</p>
      </div>
      <div className="capability-grid">
        {capabilities.map((capability) => (
          <article
            key={capability.id}
            className={active === capability.id ? 'capability-card active reveal' : 'capability-card reveal'}
            onMouseEnter={() => setActive(capability.id)}
            onFocus={() => setActive(capability.id)}
            tabIndex={0}
          >
            <div className="card-top"><span>{capability.number}</span><ArrowRight /></div>
            <h3>{capability.title}</h3>
            <p>{capability.description}</p>
            <div className="card-tags">{capability.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
            <strong>{capability.metric}</strong>
          </article>
        ))}
      </div>
    </section>
  );
}

function Configurator() {
  const [step, setStep] = useState(0);
  const [state, setState] = useState<ConfiguratorState>(initialConfiguratorState);
  const [result, setResult] = useState<Recommendation | null>(null);
  const questions = [
    { key: 'business', label: '¿En qué universo opera tu negocio?', options: businessOptions },
    { key: 'objective', label: '¿Qué tiene que lograr la experiencia?', options: objectiveOptions },
    { key: 'impact', label: '¿Cuánta intensidad visual necesita?', options: impactOptions },
    { key: 'features', label: '¿Qué sistemas debería incorporar?', options: featureOptions, multi: true },
    { key: 'timeline', label: '¿En qué plazo debería estar activa?', options: timelineOptions },
    { key: 'budget', label: '¿Qué rango de inversión imaginás?', options: budgetOptions },
  ] as const;
  const current = questions[step];
  const selected = current.key === 'features' ? state.features : state[current.key];
  const canContinue = Array.isArray(selected) ? selected.length > 0 : Boolean(selected);

  const choose = (value: string) => {
    if (current.key === 'features') {
      setState((previous) => ({
        ...previous,
        features: previous.features.includes(value)
          ? previous.features.filter((feature) => feature !== value)
          : [...previous.features, value],
      }));
    } else {
      setState((previous) => ({ ...previous, [current.key]: value }));
    }
  };

  const next = () => {
    if (step < questions.length - 1) setStep(step + 1);
    else setResult(buildRecommendation(state));
  };

  const restart = () => {
    setResult(null);
    setState(initialConfiguratorState);
    setStep(0);
  };

  return (
    <section id="configurador" className="configurator section">
      <div className="config-head reveal">
        <div><span className="eyebrow">Motor de recomendación / local / privado</span><h2>Configurá tu<br /><em>próxima señal.</em></h2></div>
        <p>Seis decisiones. Un pasaporte de proyecto con alcance, inversión y próximo paso. Sin formularios eternos.</p>
      </div>
      <div className="config-shell reveal">
        <header>
          <div><i /><span>NODO CONFIG / NC-01</span></div>
          <span>{result ? 'PASAPORTE GENERADO' : `PASO ${step + 1} DE ${questions.length}`}</span>
        </header>
        {!result ? (
          <div className="config-body">
            <aside>
              {questions.map((question, index) => (
                <div key={question.key} className={index === step ? 'active' : index < step ? 'done' : ''}>
                  <span>{index < step ? <Check size={13} /> : String(index + 1).padStart(2, '0')}</span>
                  <p>{question.label.replace('¿', '').split(' ').slice(0, 3).join(' ')}</p>
                </div>
              ))}
            </aside>
            <main>
              <div className="config-progress"><i style={{ width: `${((step + 1) / questions.length) * 100}%` }} /></div>
              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} className="question">
                  <span>0{step + 1} / DEFINICIÓN</span>
                  <h3>{current.label}</h3>
                  <div className="options">
                    {current.options.map((option) => {
                      const isSelected = Array.isArray(selected) ? selected.includes(option) : selected === option;
                      return <button key={option} className={isSelected ? 'selected' : ''} onClick={() => choose(option)}><i>{isSelected && <Check size={14} />}</i><span>{option}</span><ArrowRight size={16} /></button>;
                    })}
                  </div>
                </motion.div>
              </AnimatePresence>
              <footer>
                <button disabled={step === 0} onClick={() => setStep(step - 1)}>Volver</button>
                <button className="button primary" disabled={!canContinue} onClick={next}>{step === questions.length - 1 ? 'Generar pasaporte' : 'Continuar'} <ArrowRight size={17} /></button>
              </footer>
            </main>
          </div>
        ) : (
          <motion.div className="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="result-main">
              <div className="result-code"><span>PROYECTO RECOMENDADO</span><strong>{result.code}</strong></div>
              <h3>{result.package}</h3>
              <p>{result.summary}</p>
              <div className="result-metrics">
                <div><span>Compatibilidad</span><strong>{result.score}%</strong></div>
                <div><span>Inversión estimada</span><strong>{result.price}</strong></div>
                <div><span>Ventana de trabajo</span><strong>{result.timeline}</strong></div>
              </div>
              <div className="result-lists">
                <div><span>ARQUITECTURA SUGERIDA</span>{result.sections.map((item) => <p key={item}><Check size={13} />{item}</p>)}</div>
                <div><span>SISTEMAS RECOMENDADOS</span>{result.features.map((item) => <p key={item}><Zap size={13} />{item}</p>)}</div>
              </div>
            </div>
            <aside className="project-passport">
              <span>NODO CERO / PASAPORTE</span>
              <div className="passport-orbit"><b>{result.score}</b><small>MATCH</small></div>
              <dl><dt>SECTOR</dt><dd>{state.business}</dd><dt>OBJETIVO</dt><dd>{state.objective}</dd><dt>INTENSIDAD</dt><dd>{state.impact}</dd><dt>EMISIÓN</dt><dd>{new Date().toLocaleDateString('es-AR')}</dd></dl>
              <a className="button primary" href={recommendationWhatsApp(result, state)} target="_blank" rel="noreferrer">Enviar por WhatsApp <Send size={16} /></a>
              <button onClick={() => scrollToSection('contacto')}>Solicitar propuesta</button>
              <button onClick={restart}>Volver a configurar</button>
            </aside>
          </motion.div>
        )}
      </div>
    </section>
  );
}

function Cases() {
  return (
    <section id="casos" className="cases section">
      <div className="section-index reveal"><span>08</span><p>EXPERIENCIAS POSIBLES</p><i /></div>
      <div className="section-heading reveal">
        <div><span className="eyebrow">Escenarios conceptuales</span><h2>La tecnología cambia<br /><em>según el negocio.</em></h2></div>
        <p>Ejemplos ficticios para mostrar cómo una idea digital puede responder a necesidades comerciales completamente distintas.</p>
      </div>
      <div className="case-list">
        {cases.map((item) => (
          <article key={item.index} className="case-card reveal" style={{ '--case-color': item.color } as React.CSSProperties}>
            <div className="case-visual">
              <div className="case-grid" /><div className="case-orbit" />
              <span>{item.index}</span><small>{item.city}</small>
              <div className="case-screen"><i /><i /><i /></div>
            </div>
            <div className="case-copy">
              <span>{item.sector}</span><h3>{item.title}</h3><p>{item.concept}</p>
              <div>{item.signals.map((signal) => <em key={signal}>{signal}</em>)}</div>
              <button>Explorar concepto <ArrowRight size={16} /></button>
            </div>
          </article>
        ))}
      </div>
      <p className="fiction-note reveal">Los escenarios de esta sección son conceptuales y no representan resultados de clientes reales.</p>
    </section>
  );
}

function Packages() {
  return (
    <section id="paquetes" className="packages section">
      <div className="section-index reveal"><span>09</span><p>FORMAS DE TRABAJO</p><i /></div>
      <div className="section-heading reveal">
        <div><span className="eyebrow">Rangos orientativos / ARS</span><h2>Un punto de entrada<br /><em>para cada ambición.</em></h2></div>
        <p>Los rangos ayudan a decidir rápido. El alcance final se define después de entender negocio, contenido, integraciones y tiempos.</p>
      </div>
      <div className="package-grid">
        {packages.map((item, index) => (
          <article key={item.name} className={item.featured ? 'package-card featured reveal' : 'package-card reveal'}>
            <div className="package-top"><span>0{index + 1}</span>{item.featured && <strong>RECOMENDADO</strong>}</div>
            <small>{item.eyebrow}</small><h3>{item.name}</h3><div className="price">{item.price}</div>
            <p>{item.ideal}</p>
            <ul>{item.features.map((feature) => <li key={feature}><Check size={14} />{feature}</li>)}</ul>
            <footer><span>{item.timeline}</span><button onClick={() => scrollToSection('contacto')}>Consultar <ArrowRight size={16} /></button></footer>
          </article>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  const [active, setActive] = useState(0);
  return (
    <section className="testimonials section">
      <div className="testimonial-aside reveal"><span className="eyebrow">Voces de negocio / perfiles ficticios</span><strong>{String(active + 1).padStart(2, '0')} / {String(testimonials.length).padStart(2, '0')}</strong><div>{testimonials.map((_, index) => <button key={index} className={active === index ? 'active' : ''} onClick={() => setActive(index)} aria-label={`Ver testimonio ${index + 1}`} />)}</div></div>
      <AnimatePresence mode="wait">
        <motion.blockquote key={active} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
          “{testimonials[active].quote}”
          <footer><div><strong>{testimonials[active].name}</strong><span>{testimonials[active].role}</span></div><small>{testimonials[active].city}</small></footer>
        </motion.blockquote>
      </AnimatePresence>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section className="faq section">
      <div className="faq-heading reveal"><span className="eyebrow">Antes de empezar</span><h2>Preguntas<br /><em>sin vueltas.</em></h2></div>
      <div className="faq-list">
        {faqs.map(([question, answer], index) => (
          <article key={question} className={open === index ? 'open reveal' : 'reveal'}>
            <button onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index}><span>0{index + 1}</span><strong>{question}</strong><ChevronDown /></button>
            <div><p>{answer}</p></div>
          </article>
        ))}
      </div>
    </section>
  );
}

interface ContactErrors { [key: string]: string }

function Contact() {
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState<ContactErrors>({});
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const nextErrors: ContactErrors = {};
    ['nombre', 'empresa', 'whatsapp', 'email', 'proyecto', 'presupuesto'].forEach((field) => {
      if (!String(form.get(field) || '').trim()) nextErrors[field] = 'Necesitamos este dato para continuar.';
    });
    if (form.get('email') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.get('email')))) nextErrors.email = 'Ingresá un email válido.';
    setErrors(nextErrors);
    if (!Object.keys(nextErrors).length) setSent(true);
  };

  return (
    <section id="contacto" className="contact section">
      <div className="contact-copy reveal">
        <span className="eyebrow">Abrir conversación / Buenos Aires</span>
        <h2>Si la marca está lista para cambiar de escala, <em>empecemos por la señal.</em></h2>
        <p>Contanos qué está pasando, qué necesitás mover y qué ventana de inversión estás evaluando. Respondemos dentro de 48 horas hábiles.</p>
        <div className="contact-direct"><span>CANAL DIRECTO</span><a href={WHATSAPP} target="_blank" rel="noreferrer">WhatsApp / +54 9 11 5555 0190 <ArrowRight size={16} /></a><a href="mailto:hola@nodocero.ar">hola@nodocero.ar <ArrowRight size={16} /></a></div>
      </div>
      <div className="contact-form-wrap reveal">
        {sent ? (
          <div className="success-state">
            <div><Check /></div><span>SEÑAL RECIBIDA / NC-OK</span><h3>Ya tenemos el contexto.</h3><p>El próximo paso es revisar el alcance y responderte con una primera lectura comercial.</p><button className="button primary" onClick={() => setSent(false)}>Enviar otra consulta</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <div className="form-row">
              <label>Nombre *<input name="nombre" placeholder="¿Cómo te llamás?" aria-invalid={Boolean(errors.nombre)} />{errors.nombre && <small>{errors.nombre}</small>}</label>
              <label>Empresa *<input name="empresa" placeholder="Nombre de la marca" aria-invalid={Boolean(errors.empresa)} />{errors.empresa && <small>{errors.empresa}</small>}</label>
            </div>
            <div className="form-row">
              <label>WhatsApp *<input name="whatsapp" type="tel" placeholder="+54 9 11..." aria-invalid={Boolean(errors.whatsapp)} />{errors.whatsapp && <small>{errors.whatsapp}</small>}</label>
              <label>Email *<input name="email" type="email" placeholder="nombre@empresa.com" aria-invalid={Boolean(errors.email)} />{errors.email && <small>{errors.email}</small>}</label>
            </div>
            <div className="form-row">
              <label>Ciudad / provincia<input name="ciudad" placeholder="CABA, Córdoba, Mendoza..." /></label>
              <label>Tipo de proyecto *<select name="proyecto" defaultValue="" aria-invalid={Boolean(errors.proyecto)}><option value="" disabled>Seleccionar</option><option>Sitio premium</option><option>Lanzamiento / micrositio</option><option>Experiencia 3D</option><option>Cotizador / reservas</option><option>Otro</option></select>{errors.proyecto && <small>{errors.proyecto}</small>}</label>
            </div>
            <div className="form-row">
              <label>Presupuesto aproximado *<select name="presupuesto" defaultValue="" aria-invalid={Boolean(errors.presupuesto)}><option value="" disabled>Seleccionar rango</option>{budgetOptions.map((option) => <option key={option}>{option}</option>)}</select>{errors.presupuesto && <small>{errors.presupuesto}</small>}</label>
              <label>Plazo ideal<select name="plazo" defaultValue=""><option value="" disabled>Seleccionar plazo</option>{timelineOptions.map((option) => <option key={option}>{option}</option>)}</select></label>
            </div>
            <label>¿Qué debería cambiar este proyecto?<textarea name="mensaje" rows={4} placeholder="Contexto, problema, lanzamiento o meta comercial..." /></label>
            <div className="form-submit"><p>Al enviar aceptás que te contactemos por los datos indicados. No compartimos tu información.</p><button className="button primary" type="submit">Enviar señal <Send size={17} /></button></div>
          </form>
        )}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-brand"><span>NODO</span><span>CERO</span></div>
      <div className="footer-grid">
        <div><span>OFICINA</span><p>Buenos Aires, Argentina<br />Proyectos en todo el país.</p></div>
        <div><span>CONTACTO</span><a href="mailto:hola@nodocero.ar">hola@nodocero.ar</a><a href={WHATSAPP} target="_blank" rel="noreferrer">WhatsApp</a></div>
        <div><span>NAVEGACIÓN</span><button onClick={() => scrollToSection('artefactos')}>Exhibición</button><button onClick={() => scrollToSection('movimiento')}>Laboratorio</button></div>
        <div><span>SISTEMA</span><p>Sin cookies de terceros.<br />Experiencia local-first.</p></div>
      </div>
      <div className="footer-bottom"><span>© {new Date().getFullYear()} NODO CERO</span><span>ESTADO / OPERATIVO</span><button onClick={() => scrollToSection('inicio')}>VOLVER ARRIBA ↑</button></div>
    </footer>
  );
}

function CursorGlow() {
  const glow = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let frame = 0;
    let x = 0;
    let y = 0;
    const move = (event: MouseEvent) => {
      x = event.clientX;
      y = event.clientY;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        if (glow.current) glow.current.style.transform = `translate3d(${x - 210}px, ${y - 210}px, 0)`;
        frame = 0;
      });
    };
    window.addEventListener('pointermove', move, { passive: true });
    return () => {
      window.removeEventListener('pointermove', move);
      cancelAnimationFrame(frame);
    };
  }, []);
  return <div ref={glow} className="cursor-glow" aria-hidden="true" />;
}

function ScrollProgress() {
  const bar = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? window.scrollY / max : 0;
      if (bar.current) bar.current.style.transform = `scaleX(${progress})`;
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
    return () => window.removeEventListener('scroll', update);
  }, []);
  return <div ref={bar} className="scroll-progress" aria-hidden="true" />;
}

export default function App() {
  const { impactMode, reducedMotion, paused, highContrast } = useExperienceStore();

  useEffect(() => {
    document.documentElement.dataset.mode = impactMode;
    document.documentElement.classList.toggle('reduce-motion', reducedMotion);
    document.documentElement.classList.toggle('animations-paused', paused);
    document.documentElement.classList.toggle('high-contrast', highContrast);
  }, [highContrast, impactMode, paused, reducedMotion]);

  useEffect(() => {
    if (reducedMotion) return;
    const lenis = new Lenis({ duration: 1.05, smoothWheel: true, wheelMultiplier: 0.9 });
    let frame = 0;
    const raf = (time: number) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    lenis.on('scroll', ScrollTrigger.update);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, [reducedMotion]);

  useEffect(() => {
    if (reducedMotion) return;
    const context = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>('.reveal').forEach((element) => {
        gsap.fromTo(element, { y: 48, opacity: 0 }, {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: { trigger: element, start: 'top 88%', once: true },
        });
      });
    });
    return () => context.revert();
  }, [reducedMotion]);

  return (
    <>
      <Loader />
      <CursorGlow />
      <ScrollProgress />
      <Header />
      <CommandPalette />
      <ExperienceControls />
      <main id="contenido">
        <Hero />
        <SignalMarquee />
        <Manifesto />
        <BeforeAfter />
        <Capabilities />
        <DigitalArtifacts />
        <CinematicScroll />
        <MotionLaboratory />
        <ExperienceMap />
        <LivingInterfaces />
        <Configurator />
        <Cases />
        <Packages />
        <Testimonials />
        <FAQ />
        <Contact />
      </main>
      <Footer />
    </>
  );
}
